import styled from "styled-components";
import { useGetLunchMealsShortList } from "./useGetLunchMealsShortList";
import WIOSSLoader from "../../interface/WIOSSLoader";
import Form from "../../interface/Form";
import { useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import CreatableSelect from "react-select/creatable";
import Fuse from "fuse.js";
import { useGetLunchMealById } from "./useGetLunchMealById";
import FormRow from "../../interface/FormRow";
import Input from "../../interface/Input";
import AllergenToggle from "./AllergenToggle";
import MealImageEdit from "./MealImageEdit";
import Modal from "../../interface/Modal";
import ButtonNeu from "../../interface/ButtonNeu";
import { usePostLunchMeal } from "./usePostLunchMeal";
import { usePutLunchMeal } from "./usePutLunchMeal";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

const TopContainer = styled.div`
  display: flex;

  flex-direction: row;
  padding-left: 4rem;
  padding-right: 1rem;
  width: 100%;
  height: fit-content;
  min-height: 50rem;
  background-color: var(--backdrop-color);
  backdrop-filter: blur(10px);
  box-shadow: var(--shadow-div-md);
  border-radius: 1rem;
`;

const ImageContainer = styled.div`
  display: flex;
  width: 25%;
  height: fit-content;
  padding: 1rem;
  margin: 2rem;
  align-self: center;
`;

const PreviewImage = styled.img`
  border-radius: 4rem;
  object-fit: scale-down;
  box-shadow: var(--shadow-div-md);
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "grab")};
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-self: flex-end;
  margin-top: 12rem;
  gap: 2rem;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 70%;
`;

const AllergenContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const CaloriesContainer = styled.div`
  display: flex;
  width: 10rem;
  flex-direction: column;
`;
const CaloriesLabel = styled.div`
  font-size: 0.8em;
  text-align: center;
`;

function LunchMealsEdit() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState(null);
  const [selectedMealId, setSelectedMealId] = useState(null);
  const [mealImage, setMealImage] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState("");

  const {
    data: mealListData,
    isLoading: mealListLoading,
    error: mealListError,
  } = useGetLunchMealsShortList();

  const {
    data: selectedMealData,
    isLoading: isMealLoading,
    error: mealLoadError,
  } = useGetLunchMealById(selectedMealId, !!selectedMealId);

  const { mutate: putMeal } = usePutLunchMeal();
  const { mutate: postMeal } = usePostLunchMeal();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors, isDirty },
  } = useForm();

  const queryClient = useQueryClient();

  // Watch for `mealId` changes from URL
  useEffect(() => {
    const id = searchParams.get("mealId");
    if (id && id !== selectedMealId) {
      const selectedOption = mealListData?.find((m) => m.id === id);
      if (selectedOption) {
        setSelected({
          value: selectedOption.id,
          label: selectedOption.menuItemName,
        });
        setSelectedMealId(id);
      }
    }
  }, [searchParams, selectedMealId, mealListData]);

  const normalize = (text) =>
    text
      ?.toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "");

  const fuse = useMemo(() => {
    return new Fuse(mealListData || [], {
      keys: ["menuItemName"],
      threshold: 0.4,
      ignoreLocation: true,
      includeScore: true,
      matchAllTokens: true,
    });
  }, [mealListData]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) {
      return (
        mealListData?.map((meal) => ({
          value: meal.id,
          label: meal.menuItemName,
        })) ?? []
      );
    }

    const normalizedInput = normalize(searchTerm);
    const results = fuse.search(normalizedInput);

    return results.map((res) => ({
      value: res.item.id,
      label: res.item.menuItemName,
    }));
  }, [searchTerm, fuse, mealListData]);

  const onSubmit = (data) => {
    const fullForm = {
      ...data,
      mealBase64Image: mealImage,
    };

    if (selectedMealData) {
      fullForm.id = selectedMealId; // Only include ID for PUT (updates)
    }

    const onSuccess = (res) => {
      const meal = res?.data;
      if (!meal?.id) {
        toast.error("No ID returned from server.");
        return;
      }

      setSelectedMealId(meal.id);
      setSearchParams({ mealId: meal.id });
      setSelected({ value: meal.id, label: meal.menuItemName });
      queryClient.invalidateQueries({ queryKey: ["LunchMealsShortList"] }); //Invalidate to trigger a refetch of the list

      toast.success(
        `Meal ${selectedMealData ? "updated" : "created"} successfully`
      );
    };

    const onError = (err) => {
      toast.error("Something went wrong");
      console.error("POST/PUT Error:", err);

      if (err?.response?.data) {
        console.error("Backend response:", err.response.data);
      } else if (err?.message) {
        console.error("Error message:", err.message);
      } else {
        console.error("Unknown error structure:", err);
      }
    };

    if (selectedMealData) {
      // Meal exists → PUT
      putMeal(fullForm, { onSuccess, onError });
    } else {
      // New meal → POST
      postMeal(fullForm, { onSuccess, onError });
    }
  };

  useEffect(() => {
    if (selectedMealData) {
      reset({
        menuEnglishName: selectedMealData.menuEnglishName || "",
        menuItemName: selectedMealData.menuItemName || "",
        calories: selectedMealData.calories || 0,
        dairy: selectedMealData.dairy || false,
        eggs: selectedMealData.eggs || false,
        fish: selectedMealData.fish || false,
        gluten: selectedMealData.gluten || false,
        mustard: selectedMealData.mustard || false,
        nuts: selectedMealData.nuts || false,
        sesame: selectedMealData.sesame || false,
        shellfish: selectedMealData.shellfish || false,
        soy: selectedMealData.soy || false,
        sulphites: selectedMealData.sulphites || false,
      });

      // 👇 Reset user-selected image to what's returned from backend (if any)
      setMealImage(selectedMealData.mealBase64Image ?? null);
    }
  }, [selectedMealData, reset]);

  if (mealListLoading) return <WIOSSLoader />;
  if (mealListError) return <div>Error Loading list of meals</div>;

  selectedMealData && console.log("Selected Meal", selectedMealData);
  const noMealImage = "/images/NoMealImage.png";

  return (
    <TopContainer>
      <FormContainer>
        <Form type="modal" onSubmit={handleSubmit(onSubmit)}></Form>
        <FormRow label="Select Meal">
          <CreatableSelect
            value={selected}
            inputValue={inputValue}
            onInputChange={(value, actionMeta) => {
              if (actionMeta.action === "input-change") {
                setInputValue(value);
              }
            }}
            onChange={(option, meta) => {
              if (option.__isNew__) {
                // New meal creation
                setSelected({
                  value: null,
                  label: option.label,
                });
                setSelectedMealId(null);
                reset({
                  menuEnglishName: "",
                  menuItemName: option.label,
                  calories: 0,
                  dairy: false,
                  eggs: false,
                  fish: false,
                  gluten: false,
                  mustard: false,
                  nuts: false,
                  sesame: false,
                  shellfish: false,
                  soy: false,
                  sulphites: false,
                });
                setMealImage(null);
                setSearchParams({}); // ⛔ Clear mealId from URL
              } else {
                setSelected(option);
                setSelectedMealId(option.value);
                setValue("mealId", option.value);
                setSearchParams({ mealId: option.value }); // ✅ Update URL
              }

              setInputValue("");
            }}
            options={filteredOptions}
            isClearable
            menuPortalTarget={document.body}
            styles={{
              control: (base) => ({
                ...base,
                minWidth: "26rem",
              }),
              menuPortal: (base) => ({
                ...base,
                zIndex: 9999,
              }),
              menu: (base) => ({
                ...base,
                minWidth: "26rem",
              }),
            }}
            theme={(theme) => ({
              ...theme,
              borderRadius: 4,
              colors: {
                ...theme.colors,
                primary25: "#2c2f33",
                primary: "#7289da",
                neutral0: "#23272a",
                neutral80: "#ffffff",
              },
            })}
            formatCreateLabel={(input) => `Create new meal: "${input}"`}
            isValidNewOption={(inputValue, selectValue, options) => {
              const exists = options.some(
                (opt) => normalize(opt.label) === normalize(inputValue)
              );
              return inputValue.length > 2 && !exists;
            }}
          />
        </FormRow>
        <FormRow label="English Name">
          <Input type="text" {...register("menuEnglishName")} />
        </FormRow>

        <AllergenContainer>
          <AllergenToggle name="dairy" control={control} />
          <AllergenToggle name="eggs" control={control} />
          <AllergenToggle name="fish" control={control} />
          <AllergenToggle name="soy" control={control} />
          <AllergenToggle name="nuts" control={control} />
          <AllergenToggle name="gluten" control={control} />
          <AllergenToggle name="sesame" control={control} />
          <AllergenToggle name="shellfish" control={control} />
          <AllergenToggle name="sulphites" control={control} />
          <AllergenToggle name="mustard" control={control} />
          <CaloriesContainer>
            <Input type="number" {...register("calories")} />
            <CaloriesLabel>Calories</CaloriesLabel>
          </CaloriesContainer>
        </AllergenContainer>
        <ButtonContainer>
          <ButtonNeu
            onClick={handleSubmit(onSubmit)}
            disabled={putMeal.isLoading || postMeal.isLoading}
          >
            {selectedMealData ? "UPDATE" : "NEW MEAL"}
          </ButtonNeu>
          <ButtonNeu $variation="danger">DELETE</ButtonNeu>
        </ButtonContainer>
      </FormContainer>
      <Modal>
        <Modal.Open opens="mealImage">
          <ImageContainer>
            <PreviewImage
              src={mealImage || noMealImage}
              $disabled={isDirty}
              title={
                isDirty
                  ? "Save changes before dragging"
                  : "Drag to assign meal to a day"
              }
              draggable
              onDragStart={(e) => {
                if (isDirty) {
                  e.preventDefault();
                  toast.error("Please save the meal before dragging.");
                } else {
                  e.dataTransfer.setData("mealId", selectedMealId);
                }
              }}
            />
          </ImageContainer>
        </Modal.Open>
        <Modal.Window name="mealImage">
          <MealImageEdit onImageUpdate={setMealImage} />
        </Modal.Window>
      </Modal>
    </TopContainer>
  );
}

export default LunchMealsEdit;
